import React, { useEffect, useRef, useState } from 'react';
import * as vega from 'vega';
import Network from '../../../models/Network.js';
import URLS from '../../../urls.js';
import protocolData from './protocol.json';

const GraphTreeProtocol = ({ user, reportId }) => {
    const chartRef = useRef(null);
    const [flowData, setFlowData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadFlowData = async () => {
            // console.log('GraphTreeProtocol: loadFlowData called', { reportId, user: !!user });
            
            if (!reportId || !user) {
                // console.log('GraphTreeProtocol: Missing reportId or user, skipping API call');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);
                
                // Fetch flow data from database via API
                const url = URLS.GET_DOCUMENT_RETRIEVAL_FLOW;
                const dict = {
                    uid: user.uid,
                    report_id: reportId,
                    CLIENT_ACCESS_KEY: process.env.REACT_APP_CLIENT_ACCESS_KEY
                };

                // console.log('GraphTreeProtocol: Making API call to', url, 'with data:', dict);

                await Network.post(url, dict, (response) => {
                    // console.log('GraphTreeProtocol: API response received:', response);
                    if (Network.checkValidStatusAndResponse(response)) {
                        // console.log('GraphTreeProtocol: Setting flow data:', response.data.flowData);
                        setFlowData(response.data.flowData);
                    } else {
                        // console.error('Error fetching flow data:', response.status);
                        setError('Unable to load document retrieval flow data.');
                    }
                });
            } catch (err) {
                // console.error('Error loading flow data:', err);
                setError('Unable to load document retrieval flow data.');
            } finally {
                setLoading(false);
            }
        };

        loadFlowData();
    }, [reportId, user]);

    useEffect(() => {
        if (!chartRef.current || !flowData) return;

        // Vega specification for protocol retrieval flow
        const spec = {
            "$schema": "https://vega.github.io/schema/vega/v5.json",
            "description": "Document retrieval flow for protocol analysis",
            "width": 800,
            "height": 400,
            "padding": 20,

            "signals": [
                {
                    "name": "labels", "value": true
                },
                {
                    "name": "layout", "value": "tidy"
                },
                {
                    "name": "links", "value": "diagonal"
                },
                {
                    "name": "separation", "value": false
                }
            ],

            "data": [
                {
                    "name": "tree",
                    "values": flowData,
                    "transform": [
                        {
                            "type": "stratify",
                            "key": "id",
                            "parentKey": "parent"
                        },
                        {
                            "type": "tree",
                            "method": {"signal": "layout"},
                            "size": [{"signal": "height"}, {"signal": "width - 100"}],
                            "separation": {"signal": "separation"},
                            "as": ["y", "x", "depth", "children"]
                        }
                    ]
                },
                {
                    "name": "links",
                    "source": "tree",
                    "transform": [
                        { "type": "treelinks" },
                        {
                            "type": "linkpath",
                            "orient": "horizontal",
                            "shape": {"signal": "links"}
                        }
                    ]
                }
            ],

            "scales": [
                {
                    "name": "color",
                    "type": "ordinal",
                    "domain": ["Start", "From Tags", "Search Retrieved", "Type Codes", "Protocols Retrieved", "Linked Medications", "Protocols", "Medications", "Procedures"],
                    "range": ["#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd", "#8c564b", "#e377c2", "#7f7f7f", "#bcbd22"]
                },
                {
                    "name": "size",
                    "type": "ordinal",
                    "domain": [0, 1, 2, 3],
                    "range": [200, 150, 100, 80]
                }
            ],

            "marks": [
                {
                    "type": "path",
                    "from": {"data": "links"},
                    "encode": {
                        "update": {
                            "path": {"field": "path"},
                            "stroke": {"value": "#999"},
                            "strokeWidth": {"value": 1}
                        }
                    }
                },
                {
                    "type": "symbol",
                    "from": {"data": "tree"},
                    "encode": {
                        "enter": {
                            "stroke": {"value": "#fff"},
                            "strokeWidth": {"value": 2}
                        },
                        "update": {
                            "x": {"field": "x"},
                            "y": {"field": "y"},
                            "fill": {"scale": "color", "field": "name"},
                            "size": {"scale": "size", "field": "depth"}
                        }
                    }
                },
                {
                    "type": "text",
                    "from": {"data": "tree"},
                    "encode": {
                        "enter": {
                            "text": {"field": "name"},
                            "fontSize": {"value": 12},
                            "fontWeight": {"value": "bold"},
                            "baseline": {"value": "middle"}
                        },
                        "update": {
                            "x": {"field": "x"},
                            "y": {"field": "y"},
                            "dx": {"signal": "datum.children ? -10 : 10"},
                            "align": {"signal": "datum.children ? 'right' : 'left'"},
                            "opacity": {"signal": "labels ? 1 : 0"},
                            "fill": {"value": "#333"}
                        }
                    }
                }
            ]
        };

        // Create and render the Vega view
        const view = new vega.View(vega.parse(spec))
            .renderer('svg')
            .initialize(chartRef.current)
            .hover();

        view.runAsync();

        // Cleanup function
        return () => {
            view.finalize();
        };
    }, [flowData]);

    if (loading) {
        return (
            <div style={{ 
                borderRadius: '8px', 
                padding: '20px',
                margin: '20px 0',
                backgroundColor: 'white',
                textAlign: 'center'
            }}>
                <div>Loading document retrieval flow...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ 
                borderRadius: '8px', 
                padding: '20px',
                margin: '20px 0',
                backgroundColor: 'white',
                textAlign: 'center',
                color: '#dc3545'
            }}>
                <div>{error}</div>
            </div>
        );
    }

    if (!flowData) {
        return (
            <div style={{ 
                borderRadius: '8px', 
                padding: '20px',
                margin: '20px 0',
                backgroundColor: 'white',
                textAlign: 'center',
                color: '#6c757d'
            }}>
                <div>No document retrieval flow data available</div>
            </div>
        );
    }

    return (
        <div style={{ 
            // border: '1px solid #ccc', 
            borderRadius: '8px', 
            padding: '20px',
            margin: '20px 0',
            backgroundColor: 'white'
        }}>
            {/* <h3 style={{ marginBottom: '20px', textAlign: 'center' }}>Document Retrieval Flow</h3> */}
            {/* <p style={{ textAlign: 'center', marginBottom: '20px', color: '#666' }}>
                This diagram shows how CADMUS retrieves and organizes documentation for analysis
            </p> */}
            <div ref={chartRef}></div>
        </div>
    );
};

export default GraphTreeProtocol;
